import type { ReactNode } from "react";
import { Dialog as AriaDialog, Heading, Modal, ModalOverlay } from "react-aria-components";
import { useIntl } from "react-intl";
import { Button } from "./Button.js";
import styles from "./Dialog.module.css";

export interface ConfirmDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  title: ReactNode;
  children?: ReactNode;
  confirmLabel: string;
  onConfirm: () => void;
  tone?: "primary" | "danger";
}

export interface FormDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  title: ReactNode;
  children: ReactNode;
  /** Schaltflächen am Fuß; das Absenden steuert der Aufrufer */
  actions?: ReactNode;
  wide?: boolean;
}

/** Dialog für Formulare und Ergebnisse (z. B. Einladung anlegen, Kopplungscode anzeigen). */
export function FormDialog({ isOpen, onOpenChange, title, children, actions, wide }: FormDialogProps) {
  return (
    <ModalOverlay isOpen={isOpen} onOpenChange={onOpenChange} isDismissable className={styles.overlay}>
      <Modal className={wide ? styles.modalWide : styles.modal}>
        <AriaDialog className={styles.dialog}>
          <Heading slot="title" className={styles.title}>
            {title}
          </Heading>
          <div className={styles.form}>{children}</div>
          {actions ? <div className={styles.actions}>{actions}</div> : null}
        </AriaDialog>
      </Modal>
    </ModalOverlay>
  );
}

/** In-App-Bestätigung – ersetzt window.confirm (im Kiosk verboten). */
export function ConfirmDialog({
  isOpen,
  onOpenChange,
  title,
  children,
  confirmLabel,
  onConfirm,
  tone = "primary",
}: ConfirmDialogProps) {
  const intl = useIntl();
  return (
    <ModalOverlay isOpen={isOpen} onOpenChange={onOpenChange} isDismissable className={styles.overlay}>
      <Modal className={styles.modal}>
        <AriaDialog role="alertdialog" className={styles.dialog}>
          {({ close }) => (
            <>
              <Heading slot="title" className={styles.title}>
                {title}
              </Heading>
              {children ? <div className={styles.body}>{children}</div> : null}
              <div className={styles.actions}>
                {/* WAI-ARIA-Dialogmuster: Fokus startet auf der sicheren Aktion (destruktive Bestätigung) */}
                {/* eslint-disable-next-line jsx-a11y/no-autofocus */}
                <Button variant="secondary" autoFocus onPress={close}>
                  {intl.formatMessage({ id: "common.cancel" })}
                </Button>
                <Button
                  variant={tone}
                  onPress={() => {
                    onConfirm();
                    close();
                  }}
                >
                  {confirmLabel}
                </Button>
              </div>
            </>
          )}
        </AriaDialog>
      </Modal>
    </ModalOverlay>
  );
}
